import { actionTypes as FirebaseActions } from 'react-redux-firebase';

import { IIntegrationsState } from '../../types/integrations';
import * as AuthActions from '../actions/auth';
import * as IntegrationsActions from '../actions/integrations';

export const initialState: IIntegrationsState = {
  crm: {
    isAuthInProgress: false,
    isAuthenticated: undefined,
    isLoading: false,
    isSearching: false,
    isLoadingOpportunities: false,
    searchItems: null,
    opportunities: null,
    error: null,
  },
  email: {
    isAuthInProgress: false,
    isAuthenticated: undefined,
    isLoading: false,
    error: null,
  },
  zapier: {
    isLoading: false,
    error: null,
    wasLoaded: false,
    apiKey: '',
  },
  hubspotApp: {
    isLoading: false,
    isInstalled: false,
    error: null,
    result: null,
  },
};

const reducer = (state: IIntegrationsState = initialState, action: any): IIntegrationsState => {
  switch (action.type) {
    case IntegrationsActions.CHECK_INTEGRATIONS_REQUEST: {
      return {
        ...state,
        crm: {
          ...state.crm,
          isLoading: true,
          error: null,
        },
        email: {
          ...state.email,
          isLoading: true,
          error: null,
        },
      };
    }
    case IntegrationsActions.CHECK_INTEGRATIONS_SUCCESS: {
      const { crm: hasCrm, email: hasEmail, metadata } = action.payload || {};
      const clientId = metadata?.crm_client_id;
      return {
        ...state,
        crm: {
          ...state.crm,
          isLoading: false,
          isAuthenticated: hasCrm,
          ...(clientId ? { clientId } : {}),
          error: null,
        },
        email: {
          ...state.email,
          isLoading: false,
          isAuthenticated: hasEmail,
          error: null,
        },
      };
    }
    case IntegrationsActions.CHECK_INTEGRATIONS_FAILURE: {
      return {
        ...state,
        crm: {
          ...state.crm,
          isLoading: false,
          isAuthenticated: false,
          error: action.payload,
        },
        email: {
          ...state.email,
          isLoading: false,
          isAuthenticated: false,
          error: action.payload,
        },
      };
    }
    case IntegrationsActions.CRM_OAUTH_START: {
      return {
        ...state,
        crm: {
          ...state.crm,
          isAuthInProgress: true,
          error: null,
        },
      };
    }
    case IntegrationsActions.CRM_OAUTH_END: {
      return {
        ...state,
        crm: {
          ...state.crm,
          isAuthInProgress: false,
        },
      };
    }
    case IntegrationsActions.CRM_OAUTH_SUCCESS: {
      return {
        ...state,
        crm: {
          ...state.crm,
          error: null,
        },
      };
    }
    case IntegrationsActions.CRM_OAUTH_FAILURE: {
      return {
        ...state,
        crm: {
          ...state.crm,
          error: action.payload,
        },
      };
    }
    case IntegrationsActions.SEARCH_LEADS_AND_CONTACTS_REQUEST: {
      return {
        ...state,
        crm: {
          ...state.crm,
          isSearching: true,
          searchItems: null,
          error: null,
        },
      };
    }
    case IntegrationsActions.SEARCH_LEADS_AND_CONTACTS_FAILURE: {
      return {
        ...state,
        crm: {
          ...state.crm,
          isSearching: false,
          error: action.payload,
        },
      };
    }
    case IntegrationsActions.SEARCH_LEADS_AND_CONTACTS_SUCCESS: {
      return {
        ...state,
        crm: {
          ...state.crm,
          isSearching: false,
          searchItems: action.payload,
          error: null,
        },
      };
    }
    case IntegrationsActions.CLEAR_SEARCH_ITEMS: {
      return {
        ...state,
        crm: {
          ...state.crm,
          isSearching: false,
          searchItems: null,
        },
      };
    }
    case IntegrationsActions.GET_OPPORTUNITIES_REQUEST: {
      return {
        ...state,
        crm: {
          ...state.crm,
          isLoadingOpportunities: true,
          opportunities: null,
        },
      };
    }
    case IntegrationsActions.GET_OPPORTUNITIES_SUCCESS: {
      return {
        ...state,
        crm: {
          ...state.crm,
          isLoadingOpportunities: false,
          opportunities: action.payload,
          error: null,
        },
      };
    }
    case IntegrationsActions.GET_OPPORTUNITIES_FAILURE: {
      return {
        ...state,
        crm: {
          ...state.crm,
          isLoadingOpportunities: false,
          opportunities: null,
          error: action.payload,
        },
      };
    }
    case IntegrationsActions.SET_OPPORTUNITY:
    case IntegrationsActions.CLEAR_OPPORTUNITIES: {
      return {
        ...state,
        crm: {
          ...state.crm,
          isLoadingOpportunities: false,
          opportunities: null,
          error: null,
        },
      };
    }

    case IntegrationsActions.EMAIL_OAUTH_START: {
      return {
        ...state,
        email: {
          ...state.email,
          isAuthInProgress: true,
          error: null,
        },
      };
    }
    case IntegrationsActions.EMAIL_OAUTH_SUCCESS: {
      return {
        ...state,
        email: {
          ...state.email,
          isAuthInProgress: false,
          error: null,
        },
      };
    }
    case IntegrationsActions.EMAIL_OAUTH_FAILURE: {
      return {
        ...state,
        email: {
          ...state.email,
          isAuthInProgress: false,
          error: action.payload,
        },
      };
    }
    case IntegrationsActions.GENERATE_ZAPIER_API_KEY_REQUEST: {
      return {
        ...state,
        zapier: {
          isLoading: true,
          error: null,
          apiKey: '',
        },
      };
    }
    case IntegrationsActions.GENERATE_ZAPIER_API_KEY_FAILURE: {
      return {
        ...state,
        zapier: {
          isLoading: false,
          error: action.payload,
        },
      };
    }
    case IntegrationsActions.GENERATE_ZAPIER_API_KEY_SUCCESS: {
      return {
        ...state,
        zapier: {
          ...state.zapier,
          isLoading: false,
        },
      };
    }
    case IntegrationsActions.GET_USER_API_KEY_REQUEST: {
      return {
        ...state,
        zapier: {
          ...state.zapier,
          isLoading: true,
          wasLoaded: false,
        },
      };
    }
    case IntegrationsActions.GET_USER_API_KEY_SUCCESS: {
      return {
        ...state,
        zapier: {
          ...state.zapier,
          isLoading: false,
          wasLoaded: true,
          apiKey: action.payload,
        },
      };
    }
    case IntegrationsActions.GET_USER_API_KEY_FAILURE: {
      return {
        ...state,
        zapier: {
          ...state.zapier,
          isLoading: false,
          error: action.payload,
          wasLoaded: true,
        },
      };
    }
    case FirebaseActions.LOGOUT:
    case AuthActions.ADMIN_ROLE_CHANGED:
    case AuthActions.CHANGE_ORGANIZATION_SUCCESS: {
      return initialState;
    }
    case IntegrationsActions.INSTALL_HUBSPOT_APP: {
      return {
        ...state,
        hubspotApp: {
          ...state.hubspotApp,
          isLoading: true,
          isInstalled: false,
          error: null,
          result: null,
        },
      };
    }
    case IntegrationsActions.INSTALL_HUBSPOT_APP_SUCCESS: {
      return {
        ...state,
        hubspotApp: {
          ...state.hubspotApp,
          isLoading: false,
          isInstalled: true,
          error: null,
          result: action.payload,
        },
      };
    }
    case IntegrationsActions.INSTALL_HUBSPOT_APP_FAILURE: {
      return {
        ...state,
        hubspotApp: {
          ...state.hubspotApp,
          isLoading: false,
          isInstalled: false,
          error: action.payload,
          result: null,
        },
      };
    }
    case IntegrationsActions.CLEAR_HUBSPOT_APP_STATE: {
      return {
        ...state,
        hubspotApp: {
          ...initialState.hubspotApp,
        },
      };
    }
    default:
      return state;
  }
};

export default reducer;
